import { type ChangeEvent, useEffect, useState } from "react";
import type { ZxcvbnFactory } from "@zxcvbn-ts/core";
import { EyeIcon } from "@phosphor-icons/react/Eye";
import { EyeSlashIcon } from "@phosphor-icons/react/EyeSlash";
import { Icon } from "../Icons/Icon";
import { Tooltip } from "../Feedback/Tooltip";
import { Row } from "../ComponentLayout/Row";
import { TextInput } from "./TextInput";
import { useControlPresentation } from "./Internal/useControlPresentation";
import { useTypingPause } from "../FormLayout/Internal/typingPause";
import type { InputWidth } from "./inputWidth";

interface PasswordInputProps {
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string | undefined;
  disabled?: boolean | undefined;
  width?: InputWidth | undefined;
  /** Forwarded to the underlying `<input>` for form submission / field matching. */
  name?: string | undefined;
  /**
   * Forwarded to the underlying `<input>`. Set "current-password" on a login
   * field or "new-password" on sign-up so password managers engage.
   */
  autoComplete?: string | undefined;
  /**
   * Show the zxcvbn strength label beside the field once the password reaches 8
   * characters. The field gives up room for it only while it's there. Defaults
   * to true — turn it off on login screens, where scoring an existing password
   * is pointless.
   */
  showStrength?: boolean | undefined;
  "data-testid"?: string | undefined;
}

const STRENGTH_TIP =
  "Tip: combine letters, numbers, and symbols for a stronger password (e.g. P@ssw0rd123!).";

// zxcvbn's 0–4 score → the Cargo label + colour. 0/1 also surface the tip. The
// colour is a status ramp (weak → strong), not a brand statement, so it uses the
// raw colour utility — blue is the neutral mid-tier.
function strengthMeta(score: number): {
  label: string;
  colorClass: string;
  tip: boolean;
} {
  switch (score) {
    case 0:
      return { label: "Very Weak", colorClass: "text-red", tip: true };
    case 1:
      return { label: "Weak", colorClass: "text-red", tip: true };
    case 2:
      return {
        label: "Average Strength",
        colorClass: "text-blue-text",
        tip: false,
      };
    case 3:
      return {
        label: "Pretty Strong",
        colorClass: "text-blue-text",
        tip: false,
      };
    default:
      return {
        label: "Very Strong!",
        colorClass: "text-dark-green",
        tip: false,
      };
  }
}

// zxcvbn-ts's engine is small but its dictionaries are large, so the core and
// language packs are all dynamically imported, and the configured factory is
// built once and cached across every PasswordInput.
let zxcvbnFactory: Promise<ZxcvbnFactory> | undefined;

function loadZxcvbn(): Promise<ZxcvbnFactory> {
  if (!zxcvbnFactory) {
    zxcvbnFactory = (async () => {
      const [{ ZxcvbnFactory }, common, en] = await Promise.all([
        import("@zxcvbn-ts/core"),
        import("@zxcvbn-ts/language-common"),
        import("@zxcvbn-ts/language-en"),
      ]);
      return new ZxcvbnFactory({
        dictionary: { ...common.dictionary, ...en.dictionary },
        graphs: common.adjacencyGraphs,
        translations: en.translations,
      });
    })();
  }
  return zxcvbnFactory;
}

/**
 * Scores the password with zxcvbn-ts. The engine + language packs are lazily
 * loaded and cached, so the dictionaries only download once a strength-metered
 * field actually mounts. Returns `undefined` until it's loaded / the password
 * is long enough to bother scoring.
 */
function usePasswordScore(
  password: string,
  enabled: boolean,
): number | undefined {
  const [score, setScore] = useState<number | undefined>(undefined);
  useEffect(() => {
    if (!enabled || password.length < 8) {
      setScore(undefined);
      return;
    }
    let cancelled = false;
    void loadZxcvbn().then((zxcvbn) => {
      if (!cancelled) setScore(zxcvbn.check(password).score);
    });
    return () => {
      cancelled = true;
    };
  }, [password, enabled]);
  return score;
}

/**
 * A password field with a show/hide toggle and a zxcvbn-powered strength label.
 * Built on TextInput, so it adopts the active control presentation (boxed vs
 * underlined) and the FormItem error state — wrap it in a FormItem for a label.
 */
export function PasswordInput({
  value,
  onChange,
  placeholder,
  disabled,
  width = "full",
  name,
  autoComplete,
  showStrength = true,
  "data-testid": dataTestid,
}: PasswordInputProps) {
  const [revealed, setRevealed] = useState(false);
  const score = usePasswordScore(value, showStrength);
  // The tip used to be hover-only, on a label that only exists while the
  // password is weak — so the people it was written for were the least likely
  // to find it. It opens by itself once the typing settles instead. The pause
  // matters here more than anywhere: every keystroke re-scores the password, so
  // an ungated tip would flicker through the whole of "Very Weak" → "Strong".
  const [focused, setFocused] = useState(false);
  const { settled, noteKeystroke } = useTypingPause();

  // The spacious underline (customer) fields use large value text, so a small
  // reveal icon gets lost — scale it up there; dense boxed (admin) fields keep
  // the small icon that matches their text.
  const presentation = useControlPresentation();
  const revealIconSize = presentation === "underlined" ? "lg" : "sm";

  const revealButton = (
    <button
      type="button"
      tabIndex={-1}
      onClick={() => setRevealed((v) => !v)}
      aria-label={revealed ? "Hide password" : "Show password"}
      className="flex cursor-pointer items-center text-content-faint transition-colors hover:text-content-secondary"
    >
      <Icon icon={revealed ? EyeIcon : EyeSlashIcon} size={revealIconSize} />
    </button>
  );

  const strengthLabel = showStrength && score !== undefined && (
    <div className="shrink-0">
      <StrengthLabel_ score={score} showTip={focused && settled} />
    </div>
  );

  return (
    <Row gap={3} shrinkable>
      {/* The field takes whatever the label leaves, so it fills the row on its
          own and gives up just enough room once there is a score to show. The
          label used to have a permanent 144px column instead, which cost the
          field a quarter of its width on every screen that meters a new
          password — even before anything had been typed. */}
      <div className="min-w-0 flex-1">
        <TextInput
          type={revealed ? "text" : "password"}
          value={value}
          onChange={(e) => {
            noteKeystroke();
            onChange?.(e);
          }}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder={placeholder}
          disabled={disabled}
          width={width}
          name={name}
          autoComplete={autoComplete}
          endAdornment={revealButton}
          {...(dataTestid !== undefined && { "data-testid": dataTestid })}
        />
      </div>
      {strengthLabel}
    </Row>
  );
}

function StrengthLabel_({
  score,
  showTip,
}: {
  score: number;
  /** Opens the tip without a hover — see `PasswordInput`. */
  showTip: boolean;
}) {
  const { label, colorClass, tip } = strengthMeta(score);
  const text = (
    <span className={`text-sm font-medium whitespace-nowrap ${colorClass}`}>
      {label}
    </span>
  );
  if (!tip) return text;
  return (
    <Tooltip content={STRENGTH_TIP} roomy forceOpen={showTip}>
      {text}
    </Tooltip>
  );
}
